/**
 * Verbatim prototype copy for the AI Brain shell + Criteria tree
 * (E1-brain-tree.md §1; `brainVals` / `brNewVals` / `brSaveNew` in
 * Inbox.dc.html). One place so tests and FE-brain-ops read the same strings.
 *
 * Pure module (no React) — safe to import from anywhere.
 */

export const BRAIN_TITLE = 'AI Brain';

export const BRAIN_DESCRIPTION = "JEV doesn't write answers. It reads the person, understands the question, "
    + 'chooses the sources, briefs the language model and checks its draft. It learns from feedback, and '
    + 'nothing it learns goes live until an admin approves it.';

/** `brPermNote` — the "Viewing as" pill tooltip, and the Editor read-only banner. */
export const BRAIN_PERM_NOTE = Object.freeze({
    manager: 'You can approve changes and grow the tree (Manage AI Brain), and open conversations '
        + '(Investigate conversations).',
    viewer: 'Read-only. You can see the tree and topic trends. Approving changes and growing the tree need '
        + 'the Manage AI Brain permission.',
});

/** Prototype `denied(what)`: "{what} needs the Manage AI Brain permission. Wiki admins have it." */
export function brainDeniedToast(what) {
    return `${what} needs the Manage AI Brain permission. Wiki admins have it.`;
}

/** Member gate (`brGate`, markup 194840). */
export const BRAIN_GATE = Object.freeze({
    title: 'The AI Brain is for Wiki admins and editors',
    body: 'Your role can ask the Wiki and give feedback, which is how the Brain learns. Seeing the tree needs '
        + 'the View AI Brain permission. Ask a Wiki admin if you need it.',
});

/** Tabs in prototype order. `id` is the route tab (prototype `queue` = route `review`). */
export const BRAIN_TABS = Object.freeze([
    Object.freeze({ id: 'tree', label: 'Criteria tree' }),
    Object.freeze({ id: 'review', label: 'Review queue' }),
    Object.freeze({ id: 'insights', label: 'Emotion insights' }),
    Object.freeze({ id: 'settings', label: 'Settings' }),
]);

/** Four role cards (markup 133563). `tone` picks the brain.css colour pair. */
export const BRAIN_ROLE_CARDS = Object.freeze([
    { id: 'heart', chip: 'Heart', title: 'JEV reads the person',
        body: 'Mood, how sure they need us to be, and whether they just corrected us.' },
    { id: 'brain', chip: 'Brain', title: 'JEV decides and checks',
        body: 'What is being asked, which passages to trust, and whether the draft is true to them.' },
    { id: 'driver', chip: 'Driver', title: 'JEV briefs the writer',
        body: 'Shape, length, tone, the facts that must stay exact, and when to quote word for word.' },
    { id: 'writer', chip: 'Writer', title: 'The language model writes',
        body: "Clear, correct sentences from the chosen passages, following JEV's brief." },
]);

export const BRAIN_LEGEND = Object.freeze({
    good: '90%+ in replay',
    mid: '75–89%',
    bad: 'Below 75%',
    proposed: 'Proposed',
    learned: 'Learned',
    fixed: 'Fixed',
});

/** Toasts (E1 §1.5 / §1.6, contract §3). */
export const BRAIN_TOASTS = Object.freeze({
    branchCreated: 'Branch created as a draft. Add checks, replay-test them, and it goes live when a feature uses it.',
    groupAdded: 'Group added. Use the + on it to add checks.',
    checkSaved: "Saved as a draft. It's off until it passes the replay test and you switch it on.",
    switchedOn: 'Switched on. JEV asks it from the next question. You can turn it off here at any time.',
    groupSwitchedOn: 'Group switched on. Only its active checks run.',
    deleted: 'Deleted.',
    flagged: 'Flagged as weak. The AI will draft a rewrite for you to review.',
    turnedOff: 'Turned off. You can turn it back on from this panel.',
    fixedCantTurnOff: "Fixed checks can't be turned off.",
});

/** Client-side validation, first failure wins (`brSaveNew`) — the 8 verbatim messages. */
export const BRAIN_NEW_MESSAGES = Object.freeze({
    branchName: 'Give the branch a name.',
    groupName: 'Give the group a name.',
    question: 'Write the question JEV should answer, as one full question.',
    weak: 'JEV reads counts, maths and date comparisons poorly. Let code work those out, and ask JEV about '
        + 'meaning instead.',
    twoOptions: 'Add at least two options.',
    optionMeaning: 'Every option needs "What it means" and "Not for", so JEV knows where each one stops.',
    levels: 'Describe at least level 0 and level 4.',
    yes: 'Say what a yes means.',
});

/** "How to read the tree" (detail panel, nothing selected). */
export const BRAIN_HOW_TO_READ = Object.freeze({
    title: 'How to read the tree',
    intro: 'Every box is a small question JEV answers in a fraction of a second. JEV never writes the reply. '
        + 'Its answers steer the language model that does, and the groups run top to bottom every time someone '
        + 'asks the Wiki.',
    learned: ' checks were proposed by the Brain from repeated feedback and approved by an admin.',
    proposed: " checks are drafts. They're switched off until you approve them in the review queue.",
    weak: ' checks disagree with the final answer too often. The Brain drafts a rewrite.',
    growLead: 'Grow it yourself.',
    growA: ' Use the + on any box to add a child check, pick a check and choose ',
    growB: 'Branch out',
    growC: ' to add one next to it, or start a new group or branch above the tree.',
    openQueue: 'Open the review queue',
});

export const BRAIN_DETAIL = Object.freeze({
    effectLabel: 'WHAT HAPPENS WHEN IT FIRES',
    effectFallback: 'Groups the checks below it.',
    replayCaption: 'Replay tests',
    usageCaption: 'Usage',
    notTested: 'Not tested yet',
    notRun: "Hasn't run yet",
    optionsLabel: 'OPTIONS',
    dynamicNote: 'These are your own categories. Create one and it appears here.',
    waiting: 'Waiting for approval:',
    testing: 'Replaying it against recent questions...',
    passedTitle: 'Passed the replay test',
    failedTitle: 'Failed the replay test',
    buildLabel: 'BUILD THE TREE',
    buildHelper: 'New checks start as drafts. They run only after they pass a replay test and an admin switches them on.',
    runTest: 'Run replay test',
    switchOn: 'Switch on',
    switchGroupOn: 'Switch group on',
    delete: 'Delete',
    flag: 'Flag as weak',
    turnOff: 'Turn off',
    weakFallback: 'Disagrees with the final answer too often.',
});

/** New-node modal (`brNewVals`). */
export const BRAIN_NEW_COPY = Object.freeze({
    titles: Object.freeze({
        branch: 'New branch',
        group: 'New group of checks',
        child: 'Add a child check',
        sibling: 'Branch out: add a check next to it',
    }),
    save: Object.freeze({ branch: 'Create branch', group: 'Add group', check: 'Save as draft' }),
    branchPlacement: 'A new branch under the Router, next to Ask the Wiki.',
    branchName: 'BRANCH NAME',
    groupName: 'GROUP NAME',
    branchNamePh: 'e.g. Customer email replies',
    groupNamePh: 'e.g. Compliance checks',
    description: 'DESCRIPTION',
    branchDescPh: 'What will this branch decide? For example: how to reply to customer emails.',
    groupDescPh: 'What do these checks have in common?',
    whenLabel: "ASK THIS WHEN THE PARENT'S ANSWER IS",
    whenHelper: 'Pick an answer to make this a branch that only runs for it. "Always" runs it every time the parent runs.',
    questionLabel: 'QUESTION FOR JEV',
    questionPh: "e.g. Is the person asking about a specific customer's contract?",
    questionHelper: 'Ask one thing about meaning. JEV is weak at counting, maths and comparing dates, so code handles those.',
    typeLabel: 'ANSWER TYPE',
    yesLabel: 'YES MEANS',
    yesPh: 'e.g. Names one customer and asks what was agreed',
    noLabel: 'NO, OR NOT FOR',
    noPh: 'e.g. General policy questions',
    optionsLabel: 'OPTIONS',
    optionHead: Object.freeze(['Option', 'What it means', 'Not for']),
    optionPh: Object.freeze(['Label', 'When to pick it', 'When not to']),
    otherRow: 'Other',
    otherHelper: 'Added automatically and always last, so JEV has an escape when nothing fits.',
    addOption: 'Add option',
    remove: 'Remove',
    levelsLabel: 'LEVELS',
    levelHints: Object.freeze(['e.g. Not at all', '', 'e.g. Somewhat', '', 'e.g. Completely']),
    effectLabel: 'WHEN IT FIRES',
    footer: 'Starts as a draft. Run the replay test, then switch it on. Every change is versioned and in the audit log.',
    cancel: 'Cancel',
    close: 'Close',
});
